import { logErrorMessage } from "@wss/error-tracking";
import React from "react";
import { Throwable } from "@wss/error-tracking/throwable";
import ValidationError from "../ValidationError";
import CartLimitError from "./CartLimitError";
import {
  addToCartAnalytics,
  AtcAnalyticsProps,
} from "~/analytics/AddToCart/addToCartAnalytics";
import {
  AddToCartErrorMessages,
  AddToCartErrorNames,
} from "~/common/AddToCart/Constants";

interface Params {
  product: {
    description: string;
    grouping: boolean;
    isInStock: boolean;
    originalId: string;
    outletAttributes: {
      isOutlet: boolean;
      outletProductId: number;
    };
    price: {
      price: number;
    };
    shippingType: string;
    unitOfMeasure: string;
    weightInPounds: number;
  };
  itemNumber: string;
  quantity: number;
  groupingValue?: string;
  accessories?: string;
  discountCode?: string;
  isSideBySide?: string;
  suffix?: string;
  taxcode?: string;
  selectedDescription?: string;
  gtmAtcProps: AtcAnalyticsProps;
}

const request = async ({
  product,
  itemNumber,
  accessories,
  quantity,
  discountCode,
  groupingValue,
  isSideBySide,
  suffix,
  taxcode,
  selectedDescription,
  gtmAtcProps,
}: Params): Promise<Throwable> => {
  const body = new FormData();

  const getDescription = () => {
    if (product.outletAttributes?.isOutlet) {
      return `Outlet Item #${
        product.outletAttributes.outletProductId
      } (${product.originalId.toUpperCase()})`;
    }

    return selectedDescription || product.description;
  };

  const requestItemNumber = product.outletAttributes?.isOutlet
    ? "OUTLET"
    : groupingValue || itemNumber;

  try {
    body.set("from", "add");
    body.set("auto_reorder_interval_id", "0");
    body.set("dynamicadd", "1");
    body.set("item_number", requestItemNumber);
    body.set("qty", quantity.toString());
    body.set("description", getDescription());
    body.set("group", product.grouping ? "Y" : "N");
    body.set("isSideBySide", isSideBySide || "");
    body.set("price", product.price.price.toString());
    body.set("shipping", product.shippingType);
    body.set("stock_message", product.isInStock ? "Y" : "N");
    body.set("suffix", suffix || "0");
    body.set("uom", product.unitOfMeasure);
    body.set("weight", product.weightInPounds.toString());

    if (accessories) body.set("accessories", accessories);
    if (discountCode) body.set("discountcode", discountCode);
    if (taxcode) body.set("taxcode", taxcode);

    const result = await fetch("/cart/", {
      body,
      method: "POST",
      credentials: "include",
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const json: any = await result.json();

    if (json.errorName === AddToCartErrorNames.cartItemCountLimit) {
      return {
        isError: true,
        error: new CartLimitError(
          AddToCartErrorMessages.cartItemCountLimitMessage,
        ),
      };
    }
  } catch (error) {
    return { isError: true, error: error as ValidationError };
  }

  addToCartAnalytics(gtmAtcProps, {
    itemNumber: product.outletAttributes?.isOutlet
      ? `outlet-${product.outletAttributes.outletProductId}`
      : requestItemNumber,
    quantity,
  });

  return { isError: false };
};

export default request;
